//Internal Audit first code
import React, { useState, useEffect } from "react";
import { Box, CircularProgress, Button } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import AuditList from "./InternalAuditComponents/AuditList";
import AuditModal from "./InternalAuditComponents/AuditModal";
import AuditDetailsInlineView from "./InternalAuditComponents/AuditDetailsInlineView";
import CalendarModal from "./InternalAuditComponents/CalendarModal";
import { createInternalAuditRecord } from "../../utils/CreateInternalAudit";
import { fetchAuditList } from "../../api/FetchAuditList";
import GlobalButton from "../common/GlobalButton";
import { maxWidth } from "@mui/system";

export default function InternalAuditCalendar() {
  const [selectedDate, setSelectedDate] = useState(null); // Store selected date
  const [calendarOpen, setCalendarOpen] = useState(false); // Control calendar modal
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [audits, setAudits] = useState([]); // Store fetched audits
  const [selectedAudit, setSelectedAudit] = useState(null); // Store selected audit
  const [recordStage, setRecordStage] = useState(""); // Add state for Record Stage
  const [auditStatus, setAuditStatus] = useState(""); // Add state for Audit Status
  const [refreshTrigger, setRefreshTrigger] = useState(false); // Trigger for refreshing Kanban
  const [isLoading, setIsLoading] = useState(true);
  // Add state for tracking button position
  const [buttonPosition, setButtonPosition] = useState({ x: 20, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  const handleDayClick = (date) => {
    if (date && date instanceof Date && !isNaN(date.getTime())) {
      setSelectedDate(date); // Store the selected date
      setCalendarOpen(false); // Close the calendar modal
      setOpen(true); // Open the audit modal
    } else {
      console.error("Invalid date passed to handleDayClick:", date);
    }
  };

  // Function to open calendar modal
  const handleOpenCalendar = () => {
    setCalendarOpen(true);
  };

  // Function to close calendar modal
  const handleCloseCalendar = () => {
    setCalendarOpen(false);
  };

  const fetchAudits = async (forceRefresh = false) => {
    try {
      setIsLoading(true);
      const auditsData = await fetchAuditList(forceRefresh);
      console.log("Fetched Audits Data:", auditsData); // Debug log
      setAudits(auditsData);
    } catch (error) {
      console.error("Failed to fetch audits:", error);
    } finally {
      setIsLoading(false);
    }
  };
  console.log("audits console:", audits); // Debug log to check fetched audits

  const handleSave = async (auditData) => {
    try {
      setIsLoading(true); // Show loading state while saving
      const createdAudit = await createInternalAuditRecord(auditData);
      if (createdAudit) {
        await fetchAudits(true); // Force refresh
        setRefreshTrigger((prev) => !prev); // Trigger Kanban refresh
        setTitle("");
        setRecordStage("");
        setAuditStatus("");
        setSelectedDate(null);
        setOpen(false); // Close the modal after successful creation
      }
      return createdAudit;
    } catch (error) {
      console.error("Failed to create internal audit record:", error);
      return null;
    } finally {
      setIsLoading(false); // Hide loading state
    }
  };

  const handleAuditClick = (audit) => {
    console.log("Selected Audit:", audit); // Debug log
    setSelectedAudit(audit); // Store the selected audit
  };

  const handleDetailsClose = () => {
    setSelectedAudit(null); // Clear the selected audit
    setRefreshTrigger((prev) => !prev); // Reset Kanban layout
  };

  // Use React's useEffect with visibility API to refresh data when tab becomes visible
  useEffect(() => {
    let visibilityHandler;

    // Define the visibility change handler
    if (document.visibilityState !== undefined) {
      visibilityHandler = () => {
        if (document.visibilityState === "visible") {
          console.log("Tab is now visible, refreshing data");
          fetchAudits(); // Refresh when tab becomes visible
        }
      };

      document.addEventListener("visibilitychange", visibilityHandler);
    }

    // Initial fetch
    fetchAudits();

    return () => {
      // Clean up the event listener
      if (visibilityHandler) {
        document.removeEventListener("visibilitychange", visibilityHandler);
      }
    };
  }, []);

  // Mouse event handlers for dragging
  const handleMouseDown = (e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top,
    });
  };

  const handleMouseMove = (e) => {
    if (isDragging) {
      setButtonPosition({
        x: e.clientX - dragOffset.x,
        y: e.clientY - dragOffset.y,
      });
    }
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener("mousemove", handleMouseMove);
      document.addEventListener("mouseup", handleMouseUp);
    } else {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    }

    return () => {
      document.removeEventListener("mousemove", handleMouseMove);
      document.removeEventListener("mouseup", handleMouseUp);
    };
  }, [isDragging]);

  return (
    <Box
      sx={{
        display: "flex",
        overflowX: "auto",
        gap: 1,
        p: 1,
        height: "calc(100vh - 64px)", // Adjusted height to full viewport minus header
      }}
    >
      <Box
        sx={{
          flex: selectedAudit ? "0 0 30%" : "1 1 auto",
          transition: "flex 0.3s ease-in-out",
        }}
      >
        <Box
          sx={{
            position: "fixed",
            left: buttonPosition.x + "px",
            top: buttonPosition.y + "px",
            zIndex: 1000,
            cursor: isDragging ? "grabbing" : "grab",
          }}
          onMouseDown={handleMouseDown}
        >
          <GlobalButton
            variant="contained"
            size="small"
            sx={{
              minWidth: "unset",
              width: "40px",
              height: "40px",
              padding: "8px",
              borderRadius: "50%",
              boxShadow: "0 3px 5px 2px rgba(0, 0, 0, 0.3)",
            }}
            onClick={handleOpenCalendar}
          >
            <CalendarMonthIcon />
          </GlobalButton>
        </Box>
        <AuditList
          audits={audits}
          refreshTrigger={refreshTrigger}
          onAuditClick={handleAuditClick}
        />
      </Box>
      {selectedAudit && (
        <Box
          sx={{
            flex: "0 0 65%",
            borderRadius: 1,
            boxShadow: 1,
            //bgcolor: "#4F46E5",
            p: 0,
            ml: 1,
            height: "calc(100vh - 130px)",
            overflow: "auto",
            transition: "all 0.3s ease-in-out",
          }}
        >
          <AuditDetailsInlineView
            audit={selectedAudit}
            onClose={handleDetailsClose}
            onUpdateSuccess={() => {
              //invalidateAuditCache(); // Invalidate cache on update
              fetchAudits(true);
              setSelectedAudit(null);
              setRefreshTrigger((prev) => !prev);
            }}
            allAudits={audits}
          />
        </Box>
      )}
      {/* Calendar Modal */}
      <CalendarModal
        open={calendarOpen}
        onClose={handleCloseCalendar}
        onDateChange={handleDayClick}
        selectedDate={selectedDate}
      />

      {/* Audit Creation Modal */}
      <AuditModal
        open={open}
        onClose={() => setOpen(false)}
        title={title}
        setTitle={setTitle}
        recordStage={recordStage}
        setRecordStage={setRecordStage}
        auditStatus={auditStatus}
        setAuditStatus={setAuditStatus}
        selectedDate={selectedDate}
        onSave={handleSave}
        audits={audits}
        setAudits={setAudits}
      />
    </Box>
  );
}
